import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useModule } from '../contexts/ModuleContext';
import { Wallet, Eye, DollarSign, Building2, CreditCard, Home, Globe } from 'lucide-react';
import ExpenseModal from '../components/ExpenseModal';
import { formatINR } from '../utils/currency';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtCur = (amount, currency) => {
  const val = Number(amount || 0);
  if (currency === 'USD') return `$${val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  if (currency === 'EUR') return `€${val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  if (currency === 'GBP') return `£${val.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return formatINR(val);
};

const PendingPayment = () => {
  const { user } = useAuth();
  const { activeModule } = useModule();
  const [personalExpenses, setPersonalExpenses] = useState([]);
  const [companyExpenses, setCompanyExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedExpense, setSelectedExpense] = useState(null);

  const role = user?.role;
  const effectiveModule = role === 'accounts' ? 'INTERNATIONAL' : (role === 'dgm' || role === 'senior_approver') ? 'DOMESTIC' : activeModule;

  useEffect(() => { fetchExpenses(); }, [effectiveModule]);

  const fetchExpenses = async () => {
    setLoading(true);
    try {
      const params = {};
      if (['admin', 'management', 'hr', 'bdm'].includes(role)) params.expense_type = effectiveModule;

      const [personalRes, companyRes] = await Promise.all([
        axios.get(`${API}/expenses`, { params: { ...params, settlement_status: 'reimbursement_pending', payment_mode: 'Personal' } }),
        axios.get(`${API}/expenses`, { params: { ...params, settlement_status: 'company_paid' } })
      ]);
      setPersonalExpenses(personalRes.data);
      setCompanyExpenses(companyRes.data);
    } catch (error) {
      console.error('Error fetching expenses:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReimbursement = async (expenseId) => {
    try {
      await axios.patch(`${API}/expenses/${expenseId}/settlement`, { settlement_date: new Date().toISOString().split('T')[0] });
      fetchExpenses();
      setSelectedExpense(null);
    } catch (error) {
      console.error('Error marking as reimbursed:', error);
      alert('Failed to mark expense as reimbursed');
    }
  };

  const handleDeleteExpense = async (expenseId) => {
    await axios.delete(`${API}/expenses/${expenseId}`);
    setSelectedExpense(null);
    fetchExpenses();
  };

  // Currency-grouped totals
  const personalByCurrency = {};
  personalExpenses.forEach(exp => {
    const c = exp.currency || 'INR';
    personalByCurrency[c] = (personalByCurrency[c] || 0) + exp.amount;
  });

  const companyCashByCurrency = {};
  const companyCardByCurrency = {};
  companyExpenses.forEach(exp => {
    const c = exp.currency || 'INR';
    if (exp.payment_mode === 'Company Cash') {
      companyCashByCurrency[c] = (companyCashByCurrency[c] || 0) + exp.amount;
    } else {
      companyCardByCurrency[c] = (companyCardByCurrency[c] || 0) + exp.amount;
    }
  });

  if (loading) {
    return <div className="flex items-center justify-center h-screen"><div className="text-gray-500">Loading...</div></div>;
  }

  return (
    <div className="p-6 md:p-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          {effectiveModule === 'DOMESTIC' ? <Home className="w-5 h-5 text-indigo-500" /> : <Globe className="w-5 h-5 text-emerald-500" />}
          Accounts & Settlement
        </h1>
        <p className="text-sm text-gray-500 mt-1">{effectiveModule === 'DOMESTIC' ? 'Domestic' : 'International'} reimbursements and company-paid expenses</p>
      </div>

      {/* Personal Reimbursements */}
      <div className="mb-8">
        <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">Personal Reimbursements</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-blue-50 rounded-lg flex items-center justify-center">
                <Wallet className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <div className="text-xl font-bold text-gray-900">{personalExpenses.length}</div>
                <div className="text-xs text-gray-500">Pending Reimbursements</div>
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-indigo-50 rounded-lg flex items-center justify-center">
                <DollarSign className="w-4 h-4 text-indigo-600" />
              </div>
              <div>
                {Object.entries(personalByCurrency).map(([cur, total]) => (
                  <div key={cur} className="text-xl font-bold text-gray-900">{fmtCur(total, cur)}</div>
                ))}
                {Object.keys(personalByCurrency).length === 0 && <div className="text-xl font-bold text-gray-400">—</div>}
                <div className="text-xs text-gray-500">Total to reimburse</div>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Employee</th>
                  <th>Brand / Division</th>
                  <th>Category</th>
                  <th>Currency</th>
                  <th>Amount</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {personalExpenses.length === 0 ? (
                  <tr><td colSpan="7" className="text-center py-8"><Wallet className="w-10 h-10 text-gray-200 mx-auto mb-2" /><p className="text-gray-400 text-sm">No pending reimbursements</p></td></tr>
                ) : (
                  personalExpenses.map((expense) => (
                    <tr key={expense.id} data-testid={`expense-row-${expense.id}`}>
                      <td className="whitespace-nowrap">{new Date(expense.updated_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</td>
                      <td className="font-medium">{expense.employee_name}</td>
                      <td><span className="text-xs font-medium px-2 py-0.5 rounded bg-slate-100 text-slate-600">{expense.brand_division || 'N/A'}</span></td>
                      <td>{expense.category}</td>
                      <td className="text-xs text-gray-500">{expense.currency || 'INR'}</td>
                      <td className="font-semibold">{fmtCur(expense.amount, expense.currency || 'INR')}</td>
                      <td>
                        <button onClick={() => setSelectedExpense(expense)}
                          className="text-indigo-600 hover:text-indigo-800 font-medium text-xs flex items-center gap-1"
                          data-testid={`process-payment-${expense.id}`}
                        ><Eye className="w-3.5 h-3.5" />Process</button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Company-Paid Expenses */}
      <div>
        <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">Company-Paid Expenses</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-emerald-50 rounded-lg flex items-center justify-center">
                <Building2 className="w-4 h-4 text-emerald-600" />
              </div>
              <div>
                <div className="text-xl font-bold text-gray-900">{companyExpenses.length}</div>
                <div className="text-xs text-gray-500">Company-Paid</div>
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-emerald-50 rounded-lg flex items-center justify-center">
                <DollarSign className="w-4 h-4 text-emerald-600" />
              </div>
              <div>
                {Object.entries(companyCashByCurrency).map(([cur, total]) => (
                  <div key={cur} className="text-xl font-bold text-gray-900">{fmtCur(total, cur)}</div>
                ))}
                {Object.keys(companyCashByCurrency).length === 0 && <div className="text-xl font-bold text-gray-400">—</div>}
                <div className="text-xs text-gray-500">Company Cash</div>
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-teal-50 rounded-lg flex items-center justify-center">
                <CreditCard className="w-4 h-4 text-teal-600" />
              </div>
              <div>
                {Object.entries(companyCardByCurrency).map(([cur, total]) => (
                  <div key={cur} className="text-xl font-bold text-gray-900">{fmtCur(total, cur)}</div>
                ))}
                {Object.keys(companyCardByCurrency).length === 0 && <div className="text-xl font-bold text-gray-400">—</div>}
                <div className="text-xs text-gray-500">Company Card</div>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Employee</th>
                  <th>Brand / Division</th>
                  <th>Category</th>
                  <th>Currency</th>
                  <th>Amount</th>
                  <th>Payment</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {companyExpenses.length === 0 ? (
                  <tr><td colSpan="8" className="text-center py-8"><Building2 className="w-10 h-10 text-gray-200 mx-auto mb-2" /><p className="text-gray-400 text-sm">No company-paid expenses</p></td></tr>
                ) : (
                  companyExpenses.map((expense) => (
                    <tr key={expense.id} data-testid={`company-expense-row-${expense.id}`}>
                      <td className="whitespace-nowrap">{new Date(expense.expense_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</td>
                      <td className="font-medium">{expense.employee_name}</td>
                      <td><span className="text-xs font-medium px-2 py-0.5 rounded bg-slate-100 text-slate-600">{expense.brand_division || 'N/A'}</span></td>
                      <td>{expense.category}</td>
                      <td className="text-xs text-gray-500">{expense.currency || 'INR'}</td>
                      <td className="font-semibold">{fmtCur(expense.amount, expense.currency || 'INR')}</td>
                      <td>
                        <span className={`text-xs font-medium px-2 py-0.5 rounded ${
                          expense.payment_mode === 'Company Cash' ? 'bg-emerald-50 text-emerald-600' : 'bg-teal-50 text-teal-600'
                        }`}>{expense.payment_mode}</span>
                      </td>
                      <td>
                        <button onClick={() => setSelectedExpense(expense)}
                          className="text-gray-500 hover:text-gray-700 font-medium text-xs flex items-center gap-1"
                          data-testid={`view-company-expense-${expense.id}`}
                        ><Eye className="w-3.5 h-3.5" />View</button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {selectedExpense && (
        <ExpenseModal expense={selectedExpense} onClose={() => setSelectedExpense(null)}
          onMarkPaid={handleReimbursement}
          onDelete={user?.role === 'admin' ? handleDeleteExpense : undefined}
          showActions={selectedExpense.payment_mode === 'Personal'} actionType="payment"
        />
      )}
    </div>
  );
};

export default PendingPayment;
